// src/portal-app/team/PayRunDetailScreen.js
//
// Plan 4 Task 18 — Pay Run Detail P5 rewrite (portal-app-redesign-4-
// remaining-screens, last screen task). Was: a bare summary strip + a
// status-dependent inline button row + a grid-metric card per employee —
// no identity block, and the run's own status/period only ever appeared in
// the nav bar subtitle, never inside the screen itself.
//
// Now, following team/EmployeeDetailScreen.js (the reviewed P5 reference):
//   ProfileHero — the pay period as name, cadence as subtitle, run status
//     as the pill (a real field: PayRunResponse.status, verified against
//     backend/app/models/pay_models.py:456-471 — draft/ready/
//     partially_paid/paid/closed, the same five values the OLD subtitle
//     already rendered via `status_${run.status}`; this is not synthesised
//     the way Task 17 explicitly declined to do for Product Detail).
//     Initials/tone are derived from the cadence label (weekly/monthly/
//     daily), NOT a person's name — there is no person to represent here,
//     so the identity block colours itself consistently per cadence
//     instead (a draft's-worth-of runs of the same cadence read as the
//     same "colour family" across visits).
//   Quick actions — the SAME status-gated mutation buttons the old inline
//     bar rendered (draft: Refresh+Finalize; ready/partially_paid:
//     Re-finalize+Pay All; paid: Close; closed: Reopen), moved into
//     ProfileHero's action row. Every status produces <=2 actions, well
//     inside the 3-action cap. Delete is the one EXCEPTION — deliberately
//     NOT a quick action: it is the only genuinely destructive, irreversible
//     mutation on this screen (permanently discards a draft run), and
//     EmployeeDetailScreen's own Deactivate already established the pattern
//     of keeping a destructive action behind the nav-bar kebab instead of
//     next to lighter-weight quick actions, specifically so it is never one
//     careless tap away from firing. Delete lives behind useNavChrome's
//     kebab here for the same reason, draft-only.
//   SegmentedControl (2 options — Overview / Employees), not TabPills:
//     spec §5 P5 keeps SegmentedControl for 2-3 segments; only >=4 forces
//     TabPills. "Never invent a tab to hit a number" — two is the natural
//     split here (run-level facts vs. the per-employee breakdown), not a
//     forced count.
//   Overview tab — GroupedRows sections, all bound against
//     PayRunResponse (pay_models.py:439-471):
//       Pay Period  — period_start/period_end/cadence: DB NOT NULL, so this
//         section can never collapse (same "always-set" shape as Product
//         Detail's Product Code section).
//       Notes       — `notes: Optional[str]`, genuinely nullable (no
//         default) — a single-field section that legitimately collapses to
//         the EmptyFieldsPrompt alone on a run with no notes.
//       Finalization — `finalized_at`/`finalized_by`, both
//         `Optional[...]`, None until finalize()/re_finalize() runs — a
//         whole-section collapse on every draft.
//       Closure      — `closed_at`/`closed_by`, both `Optional[...]`, None
//         until close() runs — a whole-section collapse until the run is
//         actually closed.
//     None of these sections have an onAdd — there is no per-field edit
//     sheet for run metadata on this screen (same reasoning as Product
//     Detail's read-only Classification section: this is an actions/
//     reporting surface, not a metadata editor).
//   Employees tab — the per-employee breakdown becomes CondensedRow:
//     icon/tone are the employee's initials in their tone-coded circle,
//     the SAME `initialsFromName`/`toneFromName` formula EmployeesScreen's
//     own `initials()`/`avatarTone()` and EmployeeDetailScreen's hero use,
//     so a person's colour never differs between the Employees list, their
//     own detail hero, and their row on this screen. `value` is net pay
//     (the headline figure); `secondary` composes base pay and, when
//     present, deductions; `pill` is the line status (unpaid/partial/
//     settled). The Pay button and payment history are NOT folded into
//     CondensedRow — CondensedRow has exactly one interactive affordance
//     (onPress on the whole row, for navigation) and no slot for a second
//     embedded control, and CondensedRow is on this plan's do-not-modify
//     list — so both render as siblings directly below each row, byte-
//     identical to the pre-migration markup and payload.
//
// Verified backend field shapes (also see the task report's "field
// verification" section):
//   PayRunResponse has NO `total_owed`, `unpaid_count`, or `urgency` field
//   — those three only exist on the LIST endpoint's `to_run[]` dict
//   entries (payrun_service.py:518-529), not on the detail response this
//   screen consumes. This screen already computed `totalOwed` client-side
//   pre-migration (`Math.max(0, total_net - total_paid)`) rather than
//   reading a wire field that doesn't exist here — that was already
//   correct and is unchanged below.
//
// Bug fixed in this pass — re-finalize's reason payload:
//   `payrunService.reFinalize(payRunId, reason = null)` (services/api.js)
//   already wraps its second argument as `{ reason }` before POSTing. The
//   pre-migration `re_finalize` action config called
//   `payrunService.reFinalize(payRunId, { reason })` — passing an object
//   where the service expects the bare string — which double-wrapped the
//   payload into `{ reason: { reason: "..." } }` against a backend schema
//   requiring `reason: Optional[str]`. Every real re-finalize-with-reason
//   call would have 422'd. `reopen` (the closed-run sibling action)
//   already called its service function correctly (`reopen(payRunId,
//   reason)`, unwrapped) — re_finalize now matches it. Both existing test
//   files' assertions are updated to expect the corrected (bare-string)
//   argument.
//
// Bug fixed in this pass — ConfirmSheet's danger/destructive prop:
//   The pre-migration sheet passed `danger={confirmAction.danger}` to
//   ConfirmSheet, but shell/kit's ConfirmSheet (moved here in Plan 4 Task 1)
//   destructures `destructive`, not `danger` — so Delete's confirm button
//   never actually got its red destructive styling. Renamed to
//   `destructive` throughout; a cosmetic-only fix, no payload/behaviour
//   change.
//
// Dropped testids (disclosed): `[data-testid="status-badge"]` — StatusBadge
// is no longer rendered anywhere on this screen; the run's status is now
// ProfileHero's pill and each line item's status is CondensedRow's pill,
// both carrying the same text (colour is never the sole carrier of
// meaning) without a separate StatusBadge element. `[data-testid="action-
// delete"]` no longer lives in the screen's own DOM — it is the nav-chrome
// kebab action (see above), covered by useNavChrome's captured call args
// instead, same pattern EmployeeDetailScreen's deactivate test already
// established.
//
// No PullToRefresh: unlike ProductDetailScreen (a read-only analytics
// report, where a pull gesture is a natural "get the latest" retry),
// EmployeeDetailScreen — the closer analogue, an actionable record with
// inline mutations and open sheets — does not wrap in PullToRefresh
// either. Followed that precedent rather than ProductDetailScreen's.
//
// Permission gating — F13 Task 1. This screen previously gated nothing, so
// any user who could reach it (the hub CARD gates on can_VIEW of
// 'portal:payroll', nav/tabs.js) could also finalize, re-finalize, close,
// reopen, delete and record payments against a pay run. Every one of those
// affordances — the ProfileHero actions, the Delete kebab action, and the
// per-line Pay button — now requires can_EDIT via useAuth().canEdit, which
// the Manager default role does not hold (payroll can_view=true /
// can_edit=false).
//
// This is UX, not security. The authority lives in the backend: every
// /payrun mutation carries `require_payroll_edit` and every read carries
// `require_payroll_view` (backend/app/routers/payrun.py), enforced against
// the same role_permissions grid the login response hands AuthContext. If
// this file were deleted the endpoints would still refuse.
//
// Receives push params: { payRunId, period }
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, {
    useState, useEffect, useCallback, useRef,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { payrunService, employeeV2Service } from '../../services/api';
import { SurfaceStates, fmtMoney } from '../insights/kit';
import { GlassCard } from '../shell/LiquidGlass';
import {
    ProfileHero, SegmentedControl, GroupedRows, EmptyFieldsPrompt, CondensedRow,
    ConfirmSheet, ensureKitCss, FALLBACK_TONES,
} from '../shell/kit';
import { useNav, useNavChrome } from '../nav/NavStack';

/* ------------------------------------------------------------------ */
/* Stylesheet                                                          */
/* ------------------------------------------------------------------ */
/* Namespace is `prdt-` (pay run detail), NOT `prd-`: more/ProductsScreen.js
   already owns `prd-` by prior use and both sheets are injected globally into
   <head> with no scoping or teardown. They collided on .prd-wrap/-hero/-list/
   -form-group/-form-label/-form-input with genuinely different bodies, so
   whichever screen the user visited last silently restyled the other. */
const CSS = `
/* ---- layout ---- */
.prdt-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding-block:8px 120px;}
.prdt-hero{padding-inline:16px;margin-block-end:16px;}
.prdt-tabsrow{padding-inline:16px;margin-block-end:16px;}

/* ---- section header ---- */
.prdt-shead{padding-inline:16px;margin-block:18px 8px;}
.prdt-shead-title{font-size:11px;font-weight:700;letter-spacing:.8px;
  text-transform:uppercase;color:var(--c-textLight);}
.prdt-block{padding-inline:16px;margin-block-end:12px;}

/* ---- summary strip ---- */
.prdt-strip{display:flex;flex-wrap:wrap;gap:0;margin-inline:16px;
  margin-block-end:16px;border-radius:18px;overflow:hidden;}
.prdt-strip-cell{flex:1 1 80px;padding-block:14px;padding-inline:16px;min-width:80px;}
.prdt-strip-label{font-size:10px;font-weight:700;letter-spacing:.6px;
  text-transform:uppercase;color:var(--c-mutedLight);margin-block-end:4px;}
.prdt-strip-value{font-size:16px;font-weight:800;color:var(--c-ink);
  font-variant-numeric:tabular-nums;}
.prdt-strip-owed{color:var(--c-amber);}
.prdt-strip-green{color:var(--c-green);}

/* ---- employee list ---- */
.prdt-list{display:flex;flex-direction:column;gap:14px;padding-inline:16px;}
.prdt-emp-block{display:flex;flex-direction:column;gap:8px;}
.prdt-pay-btn{min-height:44px;padding-inline:16px;border-radius:14px;border:none;
  background:var(--c-green);font-family:inherit;font-size:13px;font-weight:700;
  color:var(--c-white);cursor:pointer;transition:opacity .15s ease;}
.prdt-pay-btn:active{opacity:.8;}
.prdt-payment-history{padding-block-start:8px;
  border-block-start:1.5px solid color-mix(in srgb,var(--c-ink) 6%,transparent);}
.prdt-payment-row{display:flex;align-items:center;justify-content:space-between;
  padding-block:4px;font-size:12px;color:var(--c-textLight);}
.prdt-payment-amount{font-weight:700;font-variant-numeric:tabular-nums;
  color:var(--c-ink);}

/* ---- payment sheet ---- */
.prdt-sheet-backdrop{position:fixed;inset:0;z-index:1000;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.prdt-sheet{width:100%;max-width:560px;margin-inline:auto;
  padding-block:24px calc(24px + var(--safe-bottom,0px));padding-inline:20px;
  box-sizing:border-box;border-radius:24px 24px 0 0;}
.prdt-sheet-drag{width:36px;height:4px;border-radius:2px;margin-inline:auto;
  margin-block-end:20px;background:color-mix(in srgb,var(--c-ink) 20%,transparent);}
.prdt-sheet-title{font-size:18px;font-weight:800;letter-spacing:-.3px;
  color:var(--c-ink);margin-block-end:2px;}
.prdt-sheet-sub{font-size:13px;color:var(--c-textLight);margin-block-end:18px;}
.prdt-sheet-summary{display:flex;gap:16px;flex-wrap:wrap;padding-block:12px;
  padding-inline:14px;border-radius:12px;margin-block-end:16px;
  background:color-mix(in srgb,var(--c-ink) 4%,transparent);}
.prdt-sheet-summary-item{font-size:12px;color:var(--c-textLight);}
.prdt-sheet-summary-val{font-weight:700;color:var(--c-ink);
  font-variant-numeric:tabular-nums;}
.prdt-form-row{display:flex;gap:12px;margin-block-end:14px;flex-wrap:wrap;}
.prdt-form-group{display:flex;flex-direction:column;gap:5px;flex:1;min-width:120px;}
.prdt-form-label{font-size:11px;font-weight:700;letter-spacing:.5px;
  text-transform:uppercase;color:var(--c-textLight);}
.prdt-form-input{min-height:44px;padding-block:10px;padding-inline:14px;
  border-radius:12px;border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:600;color:var(--c-ink);
  outline:none;box-sizing:border-box;width:100%;
  font-variant-numeric:tabular-nums;}
.prdt-form-input:focus{border-color:var(--c-green);}
.prdt-sheet-actions{display:flex;gap:10px;margin-block-start:4px;}
.prdt-sheet-cancel{flex:1;min-height:48px;border-radius:16px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:transparent;font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;}
.prdt-sheet-cancel:active{opacity:.8;}
.prdt-sheet-submit{flex:1;min-height:48px;border-radius:16px;border:none;
  background:var(--c-green);font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  transition:opacity .15s ease;}
.prdt-sheet-submit:disabled{opacity:.5;cursor:not-allowed;}
.prdt-error-banner{margin-inline:16px;margin-block-end:12px;padding-block:12px;
  padding-inline:16px;border-radius:14px;
  background:color-mix(in srgb,var(--c-red) 10%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-red) 20%,transparent);
  font-size:13px;font-weight:600;color:var(--c-red);}

/* ---- view-only notice (no can_edit on portal:payroll) ---- */
.prdt-readonly{display:flex;align-items:center;gap:8px;
  margin-inline:16px;margin-block-end:12px;
  padding-block:10px;padding-inline:12px;border-radius:12px;
  border:1px solid var(--c-border);background:var(--c-surface);
  font-size:12px;font-weight:600;color:var(--c-textLight);}
.prdt-readonly svg{flex:none;}

.prdt-spinner{width:16px;height:16px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-white) 30%,transparent);
  border-top-color:var(--c-white);
  animation:prdSpin .75s linear infinite;}
@keyframes prdSpin{to{transform:rotate(360deg);}}
@media(prefers-reduced-motion:reduce){.prdt-spinner{animation-duration:1.5s;}}
/* ---- reason input (inside confirm sheet) ---- */
.prdt-reason-input{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding-block:10px;padding-inline:14px;border-radius:12px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  margin-block-start:12px;outline:none;}
.prdt-reason-input:focus{border-color:var(--c-green);}
`;

// Injection follows the established screen pattern (see more/ProductsScreen.js):
// a `typeof document` guard so an SSR/non-DOM import can't throw, and an id on
// the tag so the sheet is identifiable in the head.
let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'prdt-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
function fmtDate(iso) {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    if (isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtPeriod(start, end) {
    return `${fmtDate(start)} – ${fmtDate(end)}`;
}

// finalized_at/closed_at are `datetime`, not `date` (pay_models.py) — they
// arrive as a full ISO timestamp (e.g. "2026-08-03T10:15:00.123456"), not
// the plain "YYYY-MM-DD" fmtDate expects. The DB column is a naive UTC
// timestamp, so a browser-local `new Date(iso)` parse would silently shift
// the TIME by the viewer's offset — showing only the calendar date (never
// a time-of-day) sidesteps that entirely, matching every other date this
// screen already renders (period/pay_date/payment_date are all day-level).
function fmtDateOnly(iso) {
    if (!iso) return null;
    return fmtDate(String(iso).split('T')[0]);
}

function sumPaid(li) {
    return (li.payments || []).reduce((acc, p) => acc + Number(p.amount || 0), 0);
}

function lineDeductions(li) {
    return Number(li.advance_deduction || 0) + Number(li.allowance_deduction || 0) + Number(li.other_deductions || 0);
}

// Same two-letter/hash convention as team/EmployeesScreen.js's own
// `initials()`/`avatarTone()` and EmployeeDetailScreen's hero — kept as a
// local copy (not imported) so this detail screen isn't coupled to the
// list screen's module, same as ProductDetailScreen's precedent. Reused
// here for BOTH the hero (keyed off the cadence label) and each employee
// row (keyed off their name), so a person's colour matches their entry
// everywhere in the app.
function initialsFromName(name = '') {
    return name.trim().split(/\s+/).map((w) => w[0] || '').join('').slice(0, 2).toUpperCase();
}
function toneFromName(name = '') {
    return FALLBACK_TONES[(name.charCodeAt(0) || 0) % FALLBACK_TONES.length];
}

const STATUS_TONE = {
    draft: 'muted',
    ready: 'amber',
    partially_paid: 'amber',
    paid: 'green',
    closed: 'muted',
};

const LINE_STATUS_TONE = {
    unpaid: 'muted',
    partial: 'amber',
    settled: 'green',
};

const PAYMENT_METHODS = ['cash', 'bank_transfer', 'cash_from_till'];

const TAB_IDS = { OVERVIEW: 'overview', EMPLOYEES: 'employees' };

/* ------------------------------------------------------------------ */
/* Local icons — inline SVG only, never emoji. Delete/Refresh/Pay reuse    */
/* the exact glyphs the pre-migration inline action bar already used;      */
/* Check is new (Finalize/Re-finalize/Close all share a "confirm/lock"     */
/* meaning; the accessible name — not the glyph — carries the distinct     */
/* meaning per action, same as every other aria-hidden icon in this kit).  */
/* ------------------------------------------------------------------ */
const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14H6L5 6" />
        <path d="M10 11v6m4-6v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);
const RefreshIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="23 4 23 10 17 10" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
);
const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const DollarIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* Summary Strip — unchanged (P6-style pre-existing zone, untouched like  */
/* ProductDetailScreen's Performance-tab HeroValue/KpiGrid).               */
/* ------------------------------------------------------------------ */
function SummaryStrip({ run, currency, t }) {
    const gross = Number(run.total_gross || 0);
    const net = Number(run.total_net || 0);
    const paid = Number(run.total_paid || 0);
    const deductions = gross - net;
    const owed = Math.max(0, net - paid);

    return (
        <GlassCard veil className="prdt-strip" data-testid="summary-strip">
            <div className="prdt-strip-cell">
                <div className="prdt-strip-label">{t('gross', {}, 'Gross')}</div>
                <div className="prdt-strip-value">{fmtMoney(gross, currency)}</div>
            </div>
            <div className="prdt-strip-cell">
                <div className="prdt-strip-label">{t('deductions', {}, 'Deductions')}</div>
                <div className="prdt-strip-value">{fmtMoney(deductions, currency)}</div>
            </div>
            <div className="prdt-strip-cell">
                <div className="prdt-strip-label">{t('net', {}, 'Net')}</div>
                <div className="prdt-strip-value prdt-strip-green">{fmtMoney(net, currency)}</div>
            </div>
            <div className="prdt-strip-cell">
                <div className="prdt-strip-label">{t('paid', {}, 'Paid')}</div>
                <div className="prdt-strip-value">{fmtMoney(paid, currency)}</div>
            </div>
            <div className="prdt-strip-cell">
                <div className="prdt-strip-label">{t('owed', {}, 'Owed')}</div>
                <div className={`prdt-strip-value${owed > 0 ? ' prdt-strip-owed' : ''}`}>{fmtMoney(owed, currency)}</div>
            </div>
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* Payment Sheet — unchanged behaviour/payload.                            */
/* ------------------------------------------------------------------ */
function PaymentSheet({ lineItem, employeeName, currency, saving, onSubmit, onClose, t }) {
    const paidAmt = sumPaid(lineItem);
    const owed = Math.max(0, Number(lineItem.net_pay || 0) - paidAmt);
    const [amount, setAmount] = useState(owed.toFixed(2));
    const [method, setMethod] = useState('bank_transfer');
    const [notes, setNotes] = useState('');

    const handleSubmit = () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) return;
        onSubmit({ amount: amt, method, notes: notes || undefined });
    };

    return (
        <div
            className="prdt-sheet-backdrop"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <GlassCard veil className="prdt-sheet" data-testid="payment-sheet">
                <div className="prdt-sheet-drag" />
                <div className="prdt-sheet-title">{t('record_payment', {}, 'Record Payment')}</div>
                <div className="prdt-sheet-sub">{employeeName}</div>

                <div className="prdt-sheet-summary">
                    <span className="prdt-sheet-summary-item">
                        {t('net', {}, 'Net')}{': '}
                        <span className="prdt-sheet-summary-val">{fmtMoney(lineItem.net_pay, currency)}</span>
                    </span>
                    <span className="prdt-sheet-summary-item">
                        {t('paid', {}, 'Paid')}{': '}
                        <span className="prdt-sheet-summary-val">{fmtMoney(paidAmt, currency)}</span>
                    </span>
                    <span className="prdt-sheet-summary-item">
                        {t('owed', {}, 'Owed')}{': '}
                        <span className="prdt-sheet-summary-val" style={{ color: owed > 0 ? 'var(--c-amber)' : undefined }}>
                            {fmtMoney(owed, currency)}
                        </span>
                    </span>
                </div>

                <div className="prdt-form-row">
                    <div className="prdt-form-group">
                        <label className="prdt-form-label">{t('amount', {}, 'Amount')}</label>
                        <input
                            className="prdt-form-input"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            data-testid="payment-amount"
                        />
                    </div>
                    <div className="prdt-form-group">
                        <label className="prdt-form-label">{t('method', {}, 'Method')}</label>
                        <select
                            className="prdt-form-input"
                            value={method}
                            onChange={(e) => setMethod(e.target.value)}
                            data-testid="payment-method"
                        >
                            {PAYMENT_METHODS.map((m) => (
                                <option key={m} value={m}>{t(m, {}, m)}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="prdt-form-group" style={{ marginBottom: 16 }}>
                    <label className="prdt-form-label">
                        {t('notes', {}, 'Notes')} ({t('optional', {}, 'optional')})
                    </label>
                    <input
                        className="prdt-form-input"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        data-testid="payment-notes"
                    />
                </div>

                <div className="prdt-sheet-actions">
                    <button className="prdt-sheet-cancel" onClick={onClose}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        className="prdt-sheet-submit"
                        onClick={handleSubmit}
                        disabled={saving || !parseFloat(amount) || parseFloat(amount) <= 0}
                        data-testid="payment-submit"
                    >
                        {saving && <span className="prdt-spinner" />}
                        {saving ? t('saving', {}, 'Saving…') : t('record_payment', {}, 'Record Payment')}
                    </button>
                </div>
            </GlassCard>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* SectionHead — title only. Run-metadata sections have no per-field edit  */
/* sheet (this is an actions/reporting surface, not a metadata editor).    */
/* ------------------------------------------------------------------ */
function SectionHead({ title }) {
    return (
        <div className="prdt-shead">
            <span className="prdt-shead-title">{title}</span>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* FieldBlock — the P5 collapse rule. Partitions `fields` into set/unset.  */
/* Set ones render as GroupedRows; every unset one collapses into ONE      */
/* EmptyFieldsPrompt. All-unset -> prompt only, no empty card above it.    */
/* None-unset -> no prompt. No onAdd — see SectionHead note above.         */
/* ------------------------------------------------------------------ */
function FieldBlock({ fields }) {
    const set = fields.filter((f) => f.value);
    const unset = fields.filter((f) => !f.value);
    const rows = set.map((f) => ({ id: f.key, label: f.label, value: f.value }));

    if (rows.length === 0 && unset.length === 0) return null;

    return (
        <div className="prdt-block">
            {rows.length > 0 && <GroupedRows rows={rows} />}
            {unset.length > 0 && (
                <EmptyFieldsPrompt fields={unset.map((f) => f.label)} />
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Main screen                                                         */
/* ------------------------------------------------------------------ */
export default function PayRunDetailScreen({ payRunId }) {
    ensureCss();
    ensureKitCss();
    const { getCurrency, canEdit } = useAuth();
    const { t, isRTL } = useLanguage();
    const { pop } = useNav();
    const currency = getCurrency();
    // can_EDIT, not can_view — reaching this screen only proves view rights.
    const canEditPayroll = canEdit('portal:payroll');

    const [run, setRun] = useState(null);
    const [employeeMap, setEmployeeMap] = useState({});
    const [surfaceStatus, setSurfaceStatus] = useState('loading');
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(TAB_IDS.OVERVIEW);

    // Action confirm states
    const [confirmAction, setConfirmAction] = useState(null); // { key, title, msg, destructive, fn }
    const [reasonValue, setReasonValue] = useState('');

    // Payment states
    const [payLine, setPayLine] = useState(null);
    const [payLoading, setPayLoading] = useState(false);

    // Bulk pay
    const [showBulkPay, setShowBulkPay] = useState(false);
    const [bulkMethod, setBulkMethod] = useState('bank_transfer');
    const [bulkLoading, setBulkLoading] = useState(false);

    const seqRef = useRef(0);
    const load = useCallback(async () => {
        const seq = ++seqRef.current;
        setSurfaceStatus('loading');
        setError('');
        try {
            const [runData, employees] = await Promise.all([
                payrunService.get(payRunId),
                employeeV2Service.listEmployees(),
            ]);
            if (seq !== seqRef.current) return;
            setRun(runData);
            const map = {};
            (employees || []).forEach((e) => { map[e.employee_id] = e; });
            setEmployeeMap(map);
            setSurfaceStatus('ready');
        } catch (err) {
            if (seq !== seqRef.current) return;
            setError(err?.response?.data?.detail || t('failed_to_load_pay_run', {}, 'Failed to load pay run'));
            setSurfaceStatus('error');
        }
    }, [payRunId, t]);

    useEffect(() => { load(); }, [load]);

    /* -------- Action handlers -------- */
    const openAction = (key) => {
        setReasonValue('');
        const configs = {
            delete: {
                title: t('delete_run', {}, 'Delete Run'),
                msg: t('delete_run_message', {}, 'This draft will be permanently deleted.'),
                destructive: true,
                requiresReason: false,
                fn: async () => {
                    await payrunService.deleteDraft(payRunId);
                    pop();
                    return null; // signal: do not update run
                },
            },
            refresh: {
                title: t('refresh_draft', {}, 'Refresh Draft'),
                msg: t('refresh_draft_message', {}, 'Recalculate pay items from latest data.'),
                destructive: false,
                requiresReason: false,
                fn: () => payrunService.refreshDraft(payRunId),
            },
            finalize: {
                title: t('finalize_run', {}, 'Finalize Run'),
                msg: t('finalize_run_message', {}, 'Lock this run for payment recording.'),
                destructive: false,
                requiresReason: false,
                fn: () => payrunService.finalize(payRunId),
            },
            re_finalize: {
                title: t('re_finalize_run', {}, 'Re-finalize Run'),
                msg: t('re_finalize_message', {}, 'Recalculate and re-lock this run.'),
                destructive: false,
                requiresReason: true,
                // See file header "Bug fixed in this pass" — reFinalize
                // already wraps its 2nd arg as { reason } internally.
                fn: (reason) => payrunService.reFinalize(payRunId, reason),
            },
            close: {
                title: t('close_run', {}, 'Close Run'),
                msg: t('close_run_message', {}, 'Archive this run. It can be reopened later.'),
                destructive: false,
                requiresReason: false,
                fn: () => payrunService.close(payRunId),
            },
            reopen: {
                title: t('reopen_run', {}, 'Reopen Run'),
                msg: t('reopen_run_message', {}, 'Reopen this run to allow further edits.'),
                destructive: false,
                requiresReason: true,
                fn: (reason) => payrunService.reopen(payRunId, reason),
            },
        };
        setConfirmAction(configs[key] || null);
    };

    const execAction = async () => {
        if (!confirmAction) return;
        try {
            setActionLoading(true);
            const result = await confirmAction.fn(
                confirmAction.requiresReason ? reasonValue : undefined
            );
            setConfirmAction(null);
            if (result !== null) {
                if (result) setRun(result);
                else await load();
            }
        } catch (err) {
            setError(err?.response?.data?.detail || t('action_failed', {}, 'Action failed.'));
            setConfirmAction(null);
        } finally {
            setActionLoading(false);
        }
    };

    const execPaySingle = async (payload) => {
        if (!payLine) return;
        try {
            setPayLoading(true);
            await payrunService.recordSinglePayment(payRunId, payLine.line_item_id, payload);
            setPayLine(null);
            await load();
        } catch (err) {
            setError(err?.response?.data?.detail || t('action_failed', {}, 'Action failed.'));
        } finally {
            setPayLoading(false);
        }
    };

    const execBulkPay = async () => {
        const lineItems = run?.line_items || [];
        const unpaid = lineItems.filter((li) => li.status !== 'settled');
        const payments = unpaid.map((li) => ({
            line_item_id: li.line_item_id,
            amount: Math.max(0, Number(li.net_pay || 0) - sumPaid(li)),
            method: bulkMethod,
        })).filter((p) => p.amount > 0);
        if (!payments.length) return;
        try {
            setBulkLoading(true);
            await payrunService.recordPayments(payRunId, payments);
            setShowBulkPay(false);
            await load();
        } catch (err) {
            setError(err?.response?.data?.detail || t('action_failed', {}, 'Action failed.'));
        } finally {
            setBulkLoading(false);
        }
    };

    /* -------- Status-dependent state -------- */
    const status = run?.status;
    // Both conditions must hold: the run must be in a payable state AND the
    // user must hold edit rights.
    const canPay = canEditPayroll && (status === 'ready' || status === 'partially_paid');
    const lineItems = run?.line_items || [];
    const unpaidItems = lineItems.filter((li) => li.status !== 'settled');
    const totalOwed = Math.max(0, Number(run?.total_net || 0) - Number(run?.total_paid || 0));

    /* -------- Nav chrome: subtitle + Delete kebab (draft only) -------- */
    useNavChrome(
        run
            ? {
                subtitle: `${t(run.cadence, {}, run.cadence)} · ${t(`status_${run.status}`, {}, run.status)}`,
                actions: canEditPayroll && run.status === 'draft'
                    ? [{
                        id: 'delete',
                        label: t('delete_run', {}, 'Delete Run'),
                        icon: <TrashIcon />,
                        onPress: () => openAction('delete'),
                    }]
                    : undefined,
            }
            : null,
        [run, t, canEditPayroll],
    );

    /* -------- ProfileHero content -------- */
    const cadenceLabel = run ? t(run.cadence, {}, run.cadence) : '';
    const heroActions = [];
    if (run && canEditPayroll) {
        if (status === 'draft') {
            heroActions.push(
                { id: 'refresh', label: t('refresh_draft', {}, 'Refresh'), icon: <RefreshIcon />, onPress: () => openAction('refresh'), testId: 'action-refresh' },
                { id: 'finalize', label: t('finalize', {}, 'Finalize'), icon: <CheckIcon />, onPress: () => openAction('finalize'), testId: 'action-finalize' },
            );
        } else if (status === 'ready' || status === 'partially_paid') {
            heroActions.push(
                { id: 're_finalize', label: t('re_finalize', {}, 'Re-finalize'), icon: <RefreshIcon />, onPress: () => openAction('re_finalize'), testId: 'action-re-finalize' },
            );
            if (totalOwed > 0 && unpaidItems.length > 0) {
                heroActions.push(
                    { id: 'pay_all', label: t('pay_all', {}, 'Pay All'), icon: <DollarIcon />, onPress: () => setShowBulkPay(true), testId: 'action-pay-all' },
                );
            }
        } else if (status === 'paid') {
            heroActions.push(
                { id: 'close', label: t('close_run', {}, 'Close'), icon: <CheckIcon />, onPress: () => openAction('close'), testId: 'action-close' },
            );
        } else if (status === 'closed') {
            heroActions.push(
                { id: 'reopen', label: t('reopen', {}, 'Reopen'), icon: <RefreshIcon />, onPress: () => openAction('reopen'), testId: 'action-reopen' },
            );
        }
    }

    /* -------- Overview tab field sections -------- */
    const periodFields = run ? [
        { key: 'period', label: t('period', {}, 'Period'), value: fmtPeriod(run.period_start, run.period_end) },
        { key: 'pay_date', label: t('pay_date', {}, 'Pay Date'), value: fmtDate(run.pay_date) },
        { key: 'cadence', label: t('cadence', {}, 'Cadence'), value: cadenceLabel },
    ] : [];
    const notesFields = run ? [
        { key: 'notes', label: t('notes', {}, 'Notes'), value: run.notes || null },
    ] : [];
    const finalizationFields = run ? [
        { key: 'finalized_at', label: t('finalized_at', {}, 'Finalized At'), value: run.finalized_at ? fmtDateOnly(run.finalized_at) : null },
        { key: 'finalized_by', label: t('finalized_by', {}, 'Finalized By'), value: run.finalized_by || null },
    ] : [];
    const closureFields = run ? [
        { key: 'closed_at', label: t('closed_at', {}, 'Closed At'), value: run.closed_at ? fmtDateOnly(run.closed_at) : null },
        { key: 'closed_by', label: t('closed_by', {}, 'Closed By'), value: run.closed_by || null },
    ] : [];

    const tabOptions = [
        { id: TAB_IDS.OVERVIEW, label: t('tab_overview', {}, 'Overview') },
        { id: TAB_IDS.EMPLOYEES, label: t('tab_employees', {}, 'Employees') },
    ];

    return (
        <div className="prdt-wrap" dir={isRTL ? 'rtl' : 'ltr'} data-testid="pay-run-detail-screen">
            {error && <div className="prdt-error-banner">{error}</div>}

            {/* View-only notice — a run whose every action has silently
                vanished reads as broken without it. */}
            {!canEditPayroll && (
                <div className="prdt-readonly" data-testid="payroll-view-only">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <span>{t('payroll_view_only', {}, 'View-only access — you cannot change pay runs.')}</span>
                </div>
            )}

            <SurfaceStates
                status={surfaceStatus}
                onRetry={load}
                emptyTitle={t('pay_run_not_found', {}, 'Pay run not found')}
            >
                {run && (
                    <>
                        {/* Identity block — a detail screen without one could be
                            about any run; this is what makes it about THIS one. */}
                        <div className="prdt-hero">
                            <ProfileHero
                                initials={initialsFromName(cadenceLabel)}
                                tone={toneFromName(cadenceLabel)}
                                name={fmtPeriod(run.period_start, run.period_end)}
                                subtitle={cadenceLabel}
                                pill={{ label: t(`status_${run.status}`, {}, run.status), tone: STATUS_TONE[run.status] || 'muted' }}
                                actions={heroActions}
                            />
                        </div>

                        {/* Section tabs */}
                        <div className="prdt-tabsrow">
                            <SegmentedControl
                                options={tabOptions}
                                value={activeTab}
                                onChange={setActiveTab}
                            />
                        </div>

                        {/* Overview tab */}
                        {activeTab === TAB_IDS.OVERVIEW && (
                            <div>
                                <SummaryStrip run={run} currency={currency} t={t} />

                                <SectionHead title={t('pay_period', {}, 'Pay Period')} />
                                <FieldBlock fields={periodFields} />

                                <SectionHead title={t('notes', {}, 'Notes')} />
                                <FieldBlock fields={notesFields} />

                                <SectionHead title={t('finalization', {}, 'Finalization')} />
                                <FieldBlock fields={finalizationFields} />

                                <SectionHead title={t('closure', {}, 'Closure')} />
                                <FieldBlock fields={closureFields} />
                            </div>
                        )}

                        {/* Employees tab */}
                        {activeTab === TAB_IDS.EMPLOYEES && (
                            <div>
                                <SectionHead title={`${t('employees', {}, 'Employees')} (${lineItems.length})`} />
                                <div className="prdt-list">
                                    {lineItems.map((li) => {
                                        const emp = employeeMap[li.employee_id];
                                        const empName = emp ? (emp.name || String(li.employee_id)) : String(li.employee_id);
                                        const paidAmt = sumPaid(li);
                                        const owed = Math.max(0, Number(li.net_pay || 0) - paidAmt);
                                        const ded = lineDeductions(li);
                                        const secondary = ded > 0
                                            ? `${t('base', {}, 'Base')} ${fmtMoney(li.base_pay, currency)} · -${fmtMoney(ded, currency)} ${t('deductions', {}, 'Deductions')}`
                                            : `${t('base', {}, 'Base')} ${fmtMoney(li.base_pay, currency)}`;

                                        return (
                                            <div key={li.line_item_id} className="prdt-emp-block">
                                                <CondensedRow
                                                    icon={initialsFromName(empName)}
                                                    tone={toneFromName(empName)}
                                                    name={empName}
                                                    value={fmtMoney(li.net_pay, currency)}
                                                    secondary={secondary}
                                                    pill={{ label: t(li.status, {}, li.status), tone: LINE_STATUS_TONE[li.status] || 'muted' }}
                                                    testId={li.line_item_id}
                                                />

                                                {(li.payments || []).length > 0 && (
                                                    <div className="prdt-payment-history">
                                                        {li.payments.map((p) => (
                                                            <div key={p.payment_id} className="prdt-payment-row">
                                                                <span>{fmtDate(p.payment_date)} · {t(p.method, {}, p.method)}</span>
                                                                <span className="prdt-payment-amount">{fmtMoney(p.amount, currency)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {canPay && li.status !== 'settled' && (
                                                    <button
                                                        type="button"
                                                        className="prdt-pay-btn"
                                                        onClick={() => setPayLine(li)}
                                                        data-testid="pay-btn"
                                                    >
                                                        {t('pay', {}, 'Pay')} · {fmtMoney(owed, currency)}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </SurfaceStates>

            {/* ── Confirm action sheet ── */}
            {confirmAction && (
                <ConfirmSheet
                    open
                    title={confirmAction.title}
                    message={confirmAction.msg}
                    onClose={() => { setConfirmAction(null); setReasonValue(''); }}
                    onConfirm={execAction}
                    loading={actionLoading}
                    destructive={confirmAction.destructive}
                    confirmLabel={t('confirm', {}, 'Confirm')}
                >
                    {confirmAction.requiresReason && (
                        <input
                            className="prdt-reason-input"
                            placeholder={t('reason', {}, 'Reason')}
                            value={reasonValue}
                            onChange={(e) => setReasonValue(e.target.value)}
                            data-testid="reason-input"
                        />
                    )}
                </ConfirmSheet>
            )}

            {/* ── Payment sheet ── */}
            {payLine && (
                <PaymentSheet
                    lineItem={payLine}
                    employeeName={(() => {
                        const e = employeeMap[payLine.employee_id];
                        return e ? (e.name || String(payLine.employee_id)) : String(payLine.employee_id);
                    })()}
                    currency={currency}
                    saving={payLoading}
                    onSubmit={execPaySingle}
                    onClose={() => setPayLine(null)}
                    t={t}
                />
            )}

            {/* ── Bulk pay confirm ── */}
            {showBulkPay && (
                <ConfirmSheet
                    open
                    title={t('pay_all_confirm_title', {}, 'Pay All Employees')}
                    message={t('pay_all_confirm_msg', {}, `Pay all unpaid items (${unpaidItems.length} employees)?`)}
                    onClose={() => setShowBulkPay(false)}
                    onConfirm={execBulkPay}
                    loading={bulkLoading}
                    confirmLabel={t('confirm', {}, 'Confirm')}
                >
                    <div style={{ marginTop: 12 }}>
                        <label className="prdt-form-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', color: 'var(--c-textLight)', display: 'block', marginBottom: 6 }}>
                            {t('payment_method', {}, 'Payment Method')}
                        </label>
                        <select
                            className="prdt-form-input"
                            value={bulkMethod}
                            onChange={(e) => setBulkMethod(e.target.value)}
                            data-testid="bulk-method"
                        >
                            {PAYMENT_METHODS.map((m) => (
                                <option key={m} value={m}>{t(m, {}, m)}</option>
                            ))}
                        </select>
                    </div>
                </ConfirmSheet>
            )}
        </div>
    );
}
